import { useState, useCallback, useRef, useEffect } from 'react';

interface UseOptimizedDropdownOptions {
  onClose?: () => void;
  onOpen?: () => void;
  closeOnEscape?: boolean;
  closeOnOutsideClick?: boolean;
}

export const useOptimizedDropdown = (options: UseOptimizedDropdownOptions = {}) => {
  const {
    onClose,
    onOpen,
    closeOnEscape = true,
    closeOnOutsideClick = true,
  } = options;

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Optimized toggle function with useCallback
  const toggle = useCallback((e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    setIsOpen(prev => {
      const newState = !prev;
      if (newState) {
        onOpen?.();
      } else {
        onClose?.();
      }
      return newState;
    });
  }, [onClose, onOpen]);

  // Optimized close function
  const close = useCallback(() => {
    setIsOpen(false);
    onClose?.();
  }, [onClose]);

  // Optimized open function
  const open = useCallback(() => {
    setIsOpen(true);
    onOpen?.();
  }, [onOpen]);

  // Handle outside click - only when dropdown is open
  useEffect(() => {
    if (!closeOnOutsideClick || !isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      
      // Check if click is outside the dropdown
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        close();
      }
    };

    // Use passive listener for better performance
    document.addEventListener('mousedown', handleClickOutside, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, closeOnOutsideClick, close]);

  // Handle escape key - only when dropdown is open
  useEffect(() => {
    if (!closeOnEscape || !isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close();
      }
    };

    document.addEventListener('keydown', handleEscape, { passive: true });
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, closeOnEscape, close]);

  return {
    isOpen,
    toggle,
    close,
    open,
    dropdownRef,
    buttonRef,
  };
};
